/**
 * The one way we parse spreadsheets that users upload (chart import, the
 * Documents and discussion-attachment previews, admin bulk import).
 *
 * These files are attacker-controlled, so every read:
 * - refuses files over SPREADSHEET_MAX_BYTES before parsing (the parser holds
 *   the whole workbook in memory, several times the file size);
 * - turns off what a preview or an import never needs: formulas, cell HTML,
 *   styles, VBA and the raw file table;
 * - wraps parser failures in SpreadsheetUnreadableError, so callers can show
 *   "can't preview" instead of crashing on a malformed file.
 *
 * xlsx (SheetJS 0.20.x, vendored under vendor/sheetjs) is loaded lazily: it is
 * large, and most screens never open a spreadsheet.
 */
import type { ParsingOptions, WorkBook } from "xlsx";

/** Matches the chart-import upload limit. */
export const SPREADSHEET_MAX_BYTES = 10 * 1024 * 1024;

export class SpreadsheetTooLargeError extends Error {
    constructor(readonly bytes: number) {
        super(`Spreadsheet is ${bytes} bytes; the limit is ${SPREADSHEET_MAX_BYTES}`);
        this.name = "SpreadsheetTooLargeError";
    }
}

export class SpreadsheetUnreadableError extends Error {
    constructor(cause: unknown) {
        super("Spreadsheet could not be read", { cause });
        this.name = "SpreadsheetUnreadableError";
    }
}

const SAFE_OPTIONS: ParsingOptions = {
    type: "array",
    cellFormula: false,
    cellHTML: false,
    cellStyles: false,
    bookVBA: false,
    bookFiles: false,
};

/** Throws SpreadsheetTooLargeError when a known size (e.g. Content-Length) is over the limit. */
export function assertSpreadsheetSize(bytes: number | null | undefined): void {
    if (bytes != null && Number.isFinite(bytes) && bytes > SPREADSHEET_MAX_BYTES) {
        throw new SpreadsheetTooLargeError(bytes);
    }
}

/**
 * Parse a workbook with the safe options (plus any extras, e.g. cellDates).
 * Returns SheetJS's utils too, so callers never import xlsx themselves.
 */
export async function readSpreadsheet(
    data: ArrayBuffer | Uint8Array,
    extra: Omit<ParsingOptions, "type"> = {},
): Promise<{ workbook: WorkBook; utils: typeof import("xlsx").utils }> {
    assertSpreadsheetSize(data.byteLength);
    const XLSX = await import("xlsx");
    try {
        return { workbook: XLSX.read(data, { ...SAFE_OPTIONS, ...extra }), utils: XLSX.utils };
    } catch (error) {
        throw new SpreadsheetUnreadableError(error);
    }
}

/** The first sheet as rows of strings (row 0 is usually the header). */
export async function readFirstSheetRows(data: ArrayBuffer | Uint8Array): Promise<string[][]> {
    const { workbook, utils } = await readSpreadsheet(data);
    const sheetName = workbook.SheetNames[0];
    const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
    if (!sheet) return [];
    const rows = utils.sheet_to_json<unknown[]>(sheet, { header: 1 });
    return rows.map((row) => (Array.isArray(row) ? row.map((cell) => String(cell ?? "")) : []));
}

/** User-facing text for a failed preview or import; null for an unexpected error. */
export function spreadsheetErrorMessage(error: unknown): string | null {
    if (error instanceof SpreadsheetTooLargeError) {
        return "This spreadsheet is too large to preview (over 10 MB). Download it instead.";
    }
    if (error instanceof SpreadsheetUnreadableError) {
        return "This spreadsheet couldn't be read. Download it instead.";
    }
    return null;
}
